import React, { useState, useEffect } from "react";
import {
  ContentLayout,
  Container,
  Header,
  SpaceBetween,
  Button,
  Table,
  Box,
  Modal,
  FormField,
  Input,
  Select,
  Flashbar,
  TextFilter,
  Pagination,
  CollectionPreferences,
  HelpPanel,
  Link,
} from "@cloudscape-design/components";
import { api } from "../api";
import { useTranslation } from "react-i18next";

function ServicesPage({ setToolsOpen, setToolsContent, isAdmin, adminViewMode }) {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [flashbarItems, setFlashbarItems] = useState([]);
  const [filteringText, setFilteringText] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortingColumn, setSortingColumn] = useState({ sortingField: "service_name" });
  const [sortingDescending, setSortingDescending] = useState(false);

  // Form state
  const [serviceName, setServiceName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingService, setEditingService] = useState(null);

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    // Set up help panel content
    setToolsContent(
      <HelpPanel header={<h2>{t("help.services.title")}</h2>}>
        <SpaceBetween size="m">
          <Box>
            <h3>{t("help.services.overview")}</h3>
            <p>{t("help.services.intro")}</p>
          </Box>

          <Box>
            <h3>{t("help.services.creating-title")}</h3>
            <ol>
              <li>{t("help.services.create-step1")}</li>
              <li>{t("help.services.create-step2")}</li>
              <li>{t("help.services.create-step3")}</li>
              <li>{t("help.services.create-step4")}</li>
            </ol>

            <Box variant="p">
              <strong>{t("help.services.naming-title")}</strong> {t("help.services.naming-desc")}
            </Box>
          </Box>

          <Box>
            <h3>{t("help.services.editing-title")}</h3>
            <ol>
              <li>{t("help.services.edit-step1")}</li>
              <li>{t("help.services.edit-step2")}</li>
              <li>{t("help.services.edit-step3")}</li>
            </ol>
          </Box>

          <Box>
            <h3>{t("help.services.deleting-title")}</h3>
            <p>{t("help.services.delete-desc")}</p>
            <ol>
              <li>{t("help.services.delete-step1")}</li>
              <li>{t("help.services.delete-step2")}</li>
              <li>{t("help.services.delete-step3")}</li>
            </ol>
          </Box>

          <Box>
            <h3>{t("help.services.categories-title")}</h3>
            <p>{t("help.services.categories-intro")}</p>
            <ul>
              <li>
                <strong>{t("help.services.label-compute")}</strong> {t("help.services.category-compute")}
              </li>
              <li>
                <strong>{t("help.services.label-storage")}</strong> {t("help.services.category-storage")}
              </li>
              <li>
                <strong>{t("help.services.label-database")}</strong> {t("help.services.category-database")}
              </li>
              <li>
                <strong>{t("help.services.label-networking")}</strong> {t("help.services.category-networking")}
              </li>
              <li>
                <strong>{t("help.services.label-security")}</strong> {t("help.services.category-security")}
              </li>
              <li>
                <strong>{t("help.services.label-analytics")}</strong> {t("help.services.category-analytics")}
              </li>
              <li>
                <strong>{t("help.services.label-ai-ml")}</strong> {t("help.services.category-ai-ml")}
              </li>
              <li>
                <strong>{t("help.services.label-dev-tools")}</strong> {t("help.services.category-dev-tools")}
              </li>
              <li>
                <strong>{t("help.services.label-management")}</strong> {t("help.services.category-management")}
              </li>
              <li>
                <strong>{t("help.services.label-other")}</strong> {t("help.services.category-other")}
              </li>
            </ul>
          </Box>

          <Box>
            <h3>{t("help.services.normalization-title")}</h3>
            <p>{t("help.services.normalization-desc")}</p>
            <p>{t("help.services.normalization-example")}</p>
          </Box>

          <Box>
            <h3>{t("help.services.why-needed-title")}</h3>
            <p>{t("help.services.why-needed-desc")}</p>
          </Box>
        </SpaceBetween>
      </HelpPanel>,
    );
  }, [setToolsContent, t]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await api.listServicesAdmin();
      setServices(data.services || []);
      setCategories((data.categories || []).map((cat) => ({ label: cat, value: cat })));
    } catch (error) {
      showFlashbar("error", t("notify.error.load-services"));
    } finally {
      setLoading(false);
    }
  };

  const showFlashbar = (type, content) => {
    setFlashbarItems([
      {
        type,
        content,
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      },
    ]);
  };

  const handleCreate = async () => {
    if (!serviceName.trim() || !selectedCategory) {
      showFlashbar("error", t("notify.error.required-fields"));
      return;
    }

    try {
      await api.createService({
        service_name: serviceName.trim(),
        category: selectedCategory.value,
      });
      showFlashbar("success", t("notify.success.service-created"));
      setShowCreateModal(false);
      setServiceName("");
      setSelectedCategory(null);
      loadServices();
    } catch (error) {
      const errorMsg = error.error || error.message || t("notify.error.create-service");
      showFlashbar("error", errorMsg);
    }
  };

  const handleEdit = async () => {
    if (!editingService || !selectedCategory) {
      return;
    }

    try {
      await api.updateService(editingService.service_name, {
        service_name: serviceName.trim(),
        category: selectedCategory.value,
      });
      showFlashbar("success", t("notify.success.service-updated"));
      setShowEditModal(false);
      setEditingService(null);
      setServiceName("");
      setSelectedCategory(null);
      loadServices();
    } catch (error) {
      const errorMsg = error.error || error.message || t("notify.error.update-service");
      showFlashbar("error", errorMsg);
    }
  };

  const handleDelete = async () => {
    if (selectedItems.length === 0) return;

    try {
      for (const service of selectedItems) {
        await api.deleteService(service.service_name);
      }
      showFlashbar("success", t("notify.success.service-deleted"));
      setShowDeleteModal(false);
      setSelectedItems([]);
      loadServices();
    } catch (error) {
      const errorMsg = error.error || error.message || t("notify.error.delete-service");
      showFlashbar("error", errorMsg);
    }
  };

  const openEditModal = () => {
    if (selectedItems.length !== 1) return;
    const service = selectedItems[0];
    setEditingService(service);
    setServiceName(service.service_name);
    setSelectedCategory({ label: service.category, value: service.category });
    setShowEditModal(true);
  };

  // Filter services
  const filteredServices = services.filter((service) => {
    if (!filteringText) return true;
    const searchText = filteringText.toLowerCase();
    return (
      service.service_name.toLowerCase().includes(searchText) ||
      service.category.toLowerCase().includes(searchText) ||
      (service.normalized_name && service.normalized_name.toLowerCase().includes(searchText))
    );
  });

  // Sort services
  const sortedServices = [...filteredServices].sort((a, b) => {
    const field = sortingColumn.sortingField || "service_name";
    const aValue = a[field] || "";
    const bValue = b[field] || "";

    const comparison = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: "base" });
    return sortingDescending ? -comparison : comparison;
  });

  // Paginate - ensure we're on a valid page
  const totalPages = Math.ceil(sortedServices.length / pageSize);
  const validPageIndex = currentPageIndex > totalPages ? 1 : currentPageIndex;
  const paginatedServices = sortedServices.slice((validPageIndex - 1) * pageSize, validPageIndex * pageSize);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("services.page.description")}
          info={
            <Link variant="info" onFollow={() => setToolsOpen(true)}>
              {t("help.action.info")}
            </Link>
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowCreateModal(true)} variant="primary">
                {t("services.action.create-service")}
              </Button>
              <Button onClick={openEditModal} disabled={selectedItems.length !== 1}>
                {t("services.action.edit-service")}
              </Button>
              {isAdmin && adminViewMode && (
                <Button onClick={() => setShowDeleteModal(true)} disabled={selectedItems.length === 0}>
                  {t("services.action.delete-service")}
                </Button>
              )}
            </SpaceBetween>
          }
        >
          {t("services.page.title")}
        </Header>
      }
    >
      <Flashbar items={flashbarItems} />

      <Container header={<Header variant="h2">{t("services.main.title")}</Header>}>
        <Table
          loading={loading}
          loadingText={t("services.main.loading")}
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          sortingColumn={sortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={({ detail }) => {
            setSortingColumn(detail.sortingColumn);
            setSortingDescending(detail.isDescending);
          }}
          columnDefinitions={[
            {
              id: "service_name",
              header: t("services.field.service-name"),
              cell: (item) => item.service_name,
              sortingField: "service_name",
            },
            {
              id: "category",
              header: t("services.field.category"),
              cell: (item) => item.category,
              sortingField: "category",
            },
            {
              id: "normalized_name",
              header: t("services.field.normalized-name"),
              cell: (item) => item.normalized_name || "-",
            },
            {
              id: "last_updated",
              header: t("services.field.last-updated"),
              cell: (item) => (item.last_updated ? new Date(item.last_updated).toLocaleString() : "-"),
              sortingField: "last_updated",
            },
          ]}
          items={paginatedServices}
          selectionType="multi"
          filter={
            <TextFilter
              filteringText={filteringText}
              filteringPlaceholder={t("services.action.search")}
              onChange={({ detail }) => {
                setFilteringText(detail.filteringText);
                setCurrentPageIndex(1); // Reset to first page when filtering
              }}
            />
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={Math.ceil(sortedServices.length / pageSize)}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          }
          preferences={
            <CollectionPreferences
              title={t("services.preferences.title")}
              confirmLabel={t("services.preferences.confirm")}
              cancelLabel={t("services.preferences.cancel")}
              preferences={{
                pageSize: pageSize,
              }}
              pageSizePreference={{
                title: t("services.preferences.page-size"),
                options: [
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ],
              }}
              onConfirm={({ detail }) => setPageSize(detail.pageSize)}
            />
          }
          empty={
            <Box textAlign="center" color="inherit">
              <b>{t("services.main.no-services")}</b>
            </Box>
          }
        />
      </Container>

      {/* Create Modal */}
      <Modal
        visible={showCreateModal}
        onDismiss={() => {
          setShowCreateModal(false);
          setServiceName("");
          setSelectedCategory(null);
        }}
        header={t("services.modal.create-title")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => {
                  setShowCreateModal(false);
                  setServiceName("");
                  setSelectedCategory(null);
                }}
              >
                {t("services.action.cancel")}
              </Button>
              <Button variant="primary" onClick={handleCreate}>
                {t("services.action.create")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label={t("services.field.service-name")}>
            <Input value={serviceName} onChange={({ detail }) => setServiceName(detail.value)} placeholder="AWS Service Name or Amazon Service Name" />
          </FormField>
          <FormField label={t("services.field.category")}>
            <Select
              selectedOption={selectedCategory}
              onChange={({ detail }) => setSelectedCategory(detail.selectedOption)}
              options={categories}
              placeholder={t("services.field.select-category")}
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Edit Modal */}
      <Modal
        visible={showEditModal}
        onDismiss={() => {
          setShowEditModal(false);
          setEditingService(null);
          setServiceName("");
          setSelectedCategory(null);
        }}
        header={t("services.modal.edit-title")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingService(null);
                  setServiceName("");
                  setSelectedCategory(null);
                }}
              >
                {t("services.action.cancel")}
              </Button>
              <Button variant="primary" onClick={handleEdit}>
                {t("services.action.save")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label={t("services.field.service-name")}>
            <Input value={serviceName} onChange={({ detail }) => setServiceName(detail.value)} />
          </FormField>
          <FormField label={t("services.field.category")}>
            <Select selectedOption={selectedCategory} onChange={({ detail }) => setSelectedCategory(detail.selectedOption)} options={categories} />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteModal}
        onDismiss={() => setShowDeleteModal(false)}
        header={t("services.modal.delete-title")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowDeleteModal(false)}>
                {t("services.action.cancel")}
              </Button>
              <Button variant="primary" onClick={handleDelete}>
                {t("services.action.delete")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>{t("services.modal.delete-confirm", { count: selectedItems.length })}</Box>
      </Modal>
    </ContentLayout>
  );
}

export default ServicesPage;
